/**
 * Who a CMS preview render is for, for the handful of places that have to behave
 * differently inside the preview frame.
 *
 * The CMS asks the editor this on every preview, and the difference is whether
 * monetization applies:
 *
 * - `subscribed` — a viewer who can watch this. Nothing is gated, the player starts
 *   from the assets the CMS sent, and no paywall or countdown covers the page.
 * - `guest` — a visitor who has not paid. The item keeps the real monetization models
 *   and plans the CMS holds, and the paywall is allowed to run.
 *
 * `subscribed` is the default so a CMS that does not send an audience — or sends one we
 * do not recognise — previews exactly as it did before this existed.
 */

export const PREVIEW_AUDIENCE = {
  SUBSCRIBED: 'subscribed',
  GUEST: 'guest',
} as const;

export type PreviewAudience = (typeof PREVIEW_AUDIENCE)[keyof typeof PREVIEW_AUDIENCE];

/** The SSR-injected globals this module reads. Undefined while rendering on the server. */
type PreviewWindow = {
  isPreview?: boolean;
  page_data?: { previewAudience?: string };
};

const previewGlobals = (): PreviewWindow | undefined =>
  typeof window === 'undefined' ? undefined : (window as unknown as PreviewWindow);

export const isPreviewRender = (): boolean => Boolean(previewGlobals()?.isPreview);

/**
 * Set by the server on the page data it injects for a preview render, from the
 * `previewAs` param the CMS puts on the preview URL.
 */
export const previewAudience = (): PreviewAudience =>
  previewGlobals()?.page_data?.previewAudience === PREVIEW_AUDIENCE.GUEST
    ? PREVIEW_AUDIENCE.GUEST
    : PREVIEW_AUDIENCE.SUBSCRIBED;

/** A preview that should behave like the published page does for a signed-out visitor. */
export const isGuestPreview = (): boolean => isPreviewRender() && previewAudience() === PREVIEW_AUDIENCE.GUEST;

/** A preview with monetization out of the way, which is what the bypasses are for. */
export const isSubscribedPreview = (): boolean =>
  isPreviewRender() && previewAudience() === PREVIEW_AUDIENCE.SUBSCRIBED;

/**
 * Whether leaving this render has to be refused.
 *
 * A preview is one render of one page. It arrived as the body of a single POST — a
 * signed, single-use one — so its URL holds nothing to come back to, and the page a link
 * points at cannot be fetched without replacing the draft with the published site. The
 * client re-fetch is skipped for exactly that reason, which is what made a navigation so
 * confusing to meet: the URL changed, the frame went on showing the modules it was
 * posted, and every click after that looked ignored.
 *
 * So a preview does not navigate. The draft stays on screen, which is what it was opened
 * for.
 */
export const isNavigationBlockedByPreview = (): boolean => isPreviewRender();

/** Anything that does not unload the page is left alone: a jump within it, a mail client. */
const leavesRenderIntact = (href: string): boolean =>
  href.startsWith('#') || /^(?!https?:)[a-z][a-z0-9+.-]*:/i.test(href);

/**
 * Refuses link clicks for as long as a preview is on screen, and returns the teardown.
 *
 * Done in the capture phase because that is what reaches a router: React Router navigates
 * only when the click was not already prevented, so one listener covers its links and
 * plain anchors alike, without either having to know about previews. A click that opens
 * its own window or tab is not refused — that leaves this render where it is.
 */
export const blockPreviewNavigation = (): (() => void) => {
  if (!isPreviewRender() || typeof document === 'undefined') return () => {};

  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target as Element | null;
    const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = anchor.getAttribute('href') ?? '';
    if (!href || leavesRenderIntact(href)) return;
    if (anchor.target && anchor.target !== '_self') return;

    event.preventDefault();
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
};
